/**
 * iter165 — Central Purchasing dashboard.
 *
 * Two tabs:
 *   1. Inbox        — staged vendor emails (M365 + manual upload),
 *                     parsed by GPT-5.2, matched + auto-applied to POs.
 *   2. POs          — cross-WO filtered PO list with bulk actions
 *                     (resend / mark-ack / mark-received / void).
 *
 * The page lives at `/purchasing` and is wired into Layout.jsx nav
 * for admin/PM/AM/service_manager/office roles.
 */
import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from "react";
import { tr } from "@/i18n";
import { toast } from "sonner";
import {
  Inbox, Plug, RefreshCw, Search, Upload, X, ChevronDown, ChevronRight, Pencil,
  CheckCircle2, AlertTriangle, Clock, ClipboardPaste, Trash2, ShoppingCart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useSearchParams, useNavigate } from "react-router-dom";
import SearchBar from "@/components/common/SearchBar";
import Segmented, { PillRail, Pill, PillDivider } from "@/components/common/Segmented";
import SubTabs from "@/components/common/SubTabs";
import StickyTop from "@/components/common/StickyTop";

import { btn } from "@/components/common/btn";
import { shortDate, money } from "@/lib/format";
import { ShipmentDialog, TrackingLink } from "@/components/purchasing/ShipmentDialog";
import VendorEmailReviewPanel from "@/components/purchasing/VendorEmailReviewPanel";
import ReceivingTab from "@/components/purchasing/ReceivingTab";
import { useAuth } from "@/auth/AuthContext";
import { setFocusTarget, FOCUS_PO } from "@/lib/deepLinkFocus";
import { SortableHeader, HeaderFilter } from "@/components/common/SortHeader";
import {
  listPurchasingInbox, ingestPurchasingInboxManual, ingestPurchasingInboxEml,
  reprocessPurchasingInbox, dismissPurchasingInbox, bulkPurchasingInboxAction,
  getM365Status, getM365AuthUrl, disconnectM365, pollM365Inbox,
  getM365Subscription, createM365Subscription, deleteM365Subscription,
  getPurchasingInboxQuota,
  listPurchasingPOs, patchPOStatus, checkInProcurementPO,
} from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ReceivePOModal, openLines } from "@/components/purchasing/ReceivePOForm";
import { poEconomics } from "@/lib/poMath";
import { POStatusPill, PO_STATUS_LABEL, PO_STATUS_ORDER, PO_STATUS_TONE } from "@/components/purchasing/POStatusPill";

const STATUS_LABELS = {
  pending_parse: { label: "Parsing…", color: "bg-slate-100 text-slate-600" },
  matched_applied: { label: "Applied", color: "bg-emerald-100 text-emerald-700" },
  matched_pending: { label: "Needs Review", color: "bg-amber-100 text-amber-700" },
  flagged: { label: "Flagged", color: "bg-red-100 text-red-700" },
  dismissed: { label: "Dismissed", color: "bg-slate-100 text-slate-400" },
};

const ACTION_LABELS = {
  acknowledge: "Acknowledge",
  ship_notice: "Ship Notice",
  quote: "Quote",
  invoice: "Invoice",
  other: "—",
};

function StatusChip({ status }) {
  const cfg = STATUS_LABELS[status] || { label: status, color: "bg-slate-100 text-slate-600" };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cfg.color}`}
      data-testid={`inbox-status-${status}`}>
      {tr(cfg.label)}
    </span>
  );
}


// ─────────────────────────────────────────────────────────────────────
// M365 Connection Card
// ─────────────────────────────────────────────────────────────────────


function M365ConnectionCard({ status, onChange }) {
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const refreshSub = useCallback(async () => {
    try {
      const res = await getM365Subscription();
      setSubscription(res.data?.subscription || null);
    } catch {
      // ignore — disabled-mode envelope is fine
    }
  }, []);

  useEffect(() => {
    if (status?.connected) refreshSub();
  }, [status?.connected, refreshSub]);

  const onConnect = async () => {
    setLoading(true);
    try {
      const res = await getM365AuthUrl();
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error(res.data?.reason || "M365 not configured");
      }
    } catch (e) {
      toast.error("Failed to start M365 OAuth");
    } finally {
      setLoading(false);
    }
  };
  const onDisconnect = async () => {
    setLoading(true);
    try {
      await disconnectM365();
      toast.success("M365 mailbox disconnected");
      setSubscription(null);
      onChange?.();
    } finally {
      setLoading(false);
    }
  };
  const onPoll = async () => {
    setLoading(true);
    try {
      const res = await pollM365Inbox(25);
      const d = res.data || {};
      if (d.status === "ok") {
        toast.success(`Polled — staged ${d.staged}, auto-applied ${d.matched_applied}`);
        onChange?.();
      } else {
        toast.error(d.reason || "Poll failed");
      }
    } finally {
      setLoading(false);
    }
  };
  const onSubscribe = async () => {
    setLoading(true);
    try {
      const res = await createM365Subscription();
      const d = res.data || {};
      if (d.status === "ok") {
        toast.success(`Push subscription created (expires ${d.expiration?.slice(0,10) || "soon"})`);
        await refreshSub();
      } else {
        toast.error(d.reason || "Subscription failed");
      }
    } finally {
      setLoading(false);
    }
  };
  const onUnsubscribe = async () => {
    setLoading(true);
    try {
      await deleteM365Subscription();
      toast.success("Push subscription removed");
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  if (!status) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
        Checking M365 status…
      </div>
    );
  }

  if (!status.configured) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm"
        data-testid="m365-card-not-configured">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold text-amber-900">M365 not configured</div>
            <div className="text-amber-800 mt-1">
              {tr("Set")} <code className="bg-amber-100 px-1 rounded">M365_CLIENT_ID</code>,{" "}
              <code className="bg-amber-100 px-1 rounded">M365_CLIENT_SECRET</code> and{" "}
              <code className="bg-amber-100 px-1 rounded">M365_TENANT_ID</code> in
              the backend env, then restart. Until then you can still ingest
              vendor emails manually via the form below.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!status.connected) {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm flex items-center justify-between gap-3"
        data-testid="m365-card-not-connected">
        <div className="flex items-center gap-3">
          <Plug className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <div className="font-semibold text-blue-900">Connect your M365 mailbox</div>
            <div className="text-blue-700 text-xs mt-0.5">
              Vendor emails (acks, ship notices, invoices) will be parsed and matched to your POs automatically.
            </div>
          </div>
        </div>
        <Button onClick={onConnect} disabled={loading} data-testid="btn-m365-connect">
          {loading ? "Opening…" : "Connect mailbox"}
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm flex items-center justify-between gap-3"
      data-testid="m365-card-connected">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        <div>
          <div className="font-semibold text-emerald-900">
            Connected — {status.connected_email || "M365 mailbox"}
          </div>
          <div className="text-emerald-700 text-xs mt-0.5">
            {subscription ? (
              <>
                Push subscription active —
                renews automatically before {subscription.expiration?.slice(0, 16).replace("T", " ") || "expiry"}.
              </>
            ) : (
              <>
                Token refreshes automatically. Click <span className="font-medium">{tr("Enable push")}</span> to switch from polling to real-time delivery.
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-end">
        {subscription ? (
          <Button variant="outline" onClick={onUnsubscribe} disabled={loading}
            data-testid="btn-m365-unsubscribe">
            {tr("Disable push")}
          </Button>
        ) : (
          <Button variant="outline" onClick={onSubscribe} disabled={loading}
            data-testid="btn-m365-subscribe">
            {tr("Enable push")}
          </Button>
        )}
        <Button variant="outline" onClick={onPoll} disabled={loading} data-testid="btn-m365-poll">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          {tr("Poll now")}
        </Button>
        <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
          onClick={onDisconnect} disabled={loading} data-testid="btn-m365-disconnect">
          {tr("Disconnect")}
        </Button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Manual Ingest Form
// ─────────────────────────────────────────────────────────────────────


function ManualIngestForm({ onIngested }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    from_email: "",
    from_name: "",
    subject: "",
    body_text: "",
  });
  const fileRef = useRef(null);

  const submit = async () => {
    if (!form.from_email && !form.subject && !form.body_text) {
      toast.error("Need at least one of: from email, subject, body");
      return;
    }
    setBusy(true);
    try {
      const res = await ingestPurchasingInboxManual(form);
      const match = res.data?.match || {};
      if (match.applied) {
        toast.success(`Applied → ${match.summary}`);
      } else if (match.status === "matched_pending") {
        toast.info("Matched to PO — needs review (low confidence)");
      } else {
        toast.warning(match.reason || "Flagged for review");
      }
      setForm({ from_email: "", from_name: "", subject: "", body_text: "" });
      setOpen(false);
      onIngested?.();
    } catch (e) {
      // 429 from rate limiter surfaces here.
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Failed to ingest email");
    } finally {
      setBusy(false);
    }
  };

  const uploadEml = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await ingestPurchasingInboxEml(file);
      const match = res.data?.match || {};
      if (match.applied) {
        toast.success(`Applied → ${match.summary}`);
      } else {
        toast.info(match.reason || "Email ingested — needs review");
      }
      onIngested?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Failed to parse .eml file");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <button type="button" className={btn("data")} onClick={() => setOpen(true)} title={tr("Paste an email")} data-testid="btn-ingest-manual">
          <ClipboardPaste className="w-3.5 h-3.5" />
          {tr("Email")}
        </button>
        <label className="cursor-pointer">
          <input ref={fileRef} type="file" accept=".eml,message/rfc822" className="hidden"
            onChange={uploadEml} data-testid="input-ingest-eml" />
          <button type="button" className={btn("data")}
            onClick={() => fileRef.current?.click()} disabled={busy}
            title={tr("Upload an .eml file")} data-testid="btn-ingest-eml">
            <Upload className="w-3.5 h-3.5" />
            {tr("Email")}
          </button>
        </label>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="manual-ingest-dialog">
          <DialogHeader>
            <DialogTitle>{tr("Paste Vendor Email")}</DialogTitle>
            <DialogDescription>
              We&apos;ll classify it with GPT-5.2 and match it to a PO.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">{tr("From Email")}</Label>
                <Input value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                  placeholder="sales@honeywell.com" data-testid="input-manual-from-email" />
              </div>
              <div>
                <Label className="text-xs">From name (optional)</Label>
                <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                  placeholder="Honeywell Sales" data-testid="input-manual-from-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{tr("Subject")}</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Order confirmation PO# 15321.001" data-testid="input-manual-subject" />
            </div>
            <div>
              <Label className="text-xs">{tr("Body")}</Label>
              <Textarea value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })}
                rows={8} placeholder={tr("Paste the vendor email body here…")}
                data-testid="input-manual-body" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>{tr("Cancel")}</Button>
            <Button onClick={submit} disabled={busy} data-testid="btn-manual-submit">
              {busy ? "Parsing…" : "Parse & match"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Inbox Table
// ─────────────────────────────────────────────────────────────────────


function InboxTab({ m365Status, onM365Changed }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [quota, setQuota] = useState(null);
  // Open POs for the forwarded-vendor-email review panel (match → apply).
  const [pos, setPos] = useState([]);
  const loadPOs = useCallback(async () => {
    try { const res = await listPurchasingPOs({ limit: 1000 }); setPos(res.data?.rows || []); } catch { /* panel degrades to manual pick */ }
  }, []);

  const refreshQuota = useCallback(async () => {
    try {
      const res = await getPurchasingInboxQuota();
      setQuota(res.data);
    } catch {
      setQuota(null);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await listPurchasingInbox(params);
      setRows(res.data?.rows || []);
    } catch {
      toast.error("Failed to load inbox");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); refreshQuota(); }, [load, refreshQuota]);
  useEffect(() => { loadPOs(); }, [loadPOs]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.subject || "").toLowerCase().includes(q) ||
      (r.from_email || "").toLowerCase().includes(q) ||
      (r.classification?.po_number || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(r => r.id)));
  };

  const runBulk = async (action) => {
    if (selected.size === 0) {
      toast.error("Select rows first");
      return;
    }
    try {
      await bulkPurchasingInboxAction([...selected], action);
      toast.success(`${action} applied to ${selected.size} row(s)`);
      setSelected(new Set());
      load();
      refreshQuota();
    } catch (e) {
      // 429 from rate limiter — surface the friendly detail.
      const detail = e?.response?.data?.detail;
      toast.error(detail || `Bulk ${action} failed`);
    }
  };

  return (
    <div className="space-y-3" data-testid="inbox-tab">
      <VendorEmailReviewPanel pos={pos} onApplied={loadPOs} />
      <M365ConnectionCard status={m365Status} onChange={() => { onM365Changed?.(); load(); refreshQuota(); }} />
      <StickyTop slot="b" testId="inbox-sticky-top">

      <div className="flex flex-wrap items-center gap-2">
        <SearchBar value={search} onChange={setSearch} placeholder={tr("Search subject, sender, PO…")} testId="input-inbox-search" width="w-72" />
        <Segmented testId="select-inbox-status" value={statusFilter} onChange={setStatusFilter} defaultKey="all"
          options={[["all", "All"], ["needs_review", "Needs review"], ["matched_applied", "Applied"], ["flagged", "Flagged"], ["dismissed", "Dismissed", true]]
            .map(([key, label, dividerBefore]) => ({ key, label, dividerBefore, testId: `inbox-filter-${key}` }))} />
        <ManualIngestForm onIngested={load} />
        {quota && (
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-500" data-testid="ingest-quota-chip">
            <span className="font-medium">Ingest Budget</span>
            <span className={`px-1.5 py-0.5 rounded num ${
              quota.daily?.used >= (quota.daily?.cap || 500) * 0.8
                ? "bg-amber-100 text-amber-800"
                : "bg-slate-100 text-slate-700"
            }`} data-testid="quota-daily">
              {quota.daily?.used || 0}/{quota.daily?.cap || 500} today
            </span>
          </div>
        )}
      </div>

      {selected.size > 0 && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-2 flex items-center gap-2 text-sm">
          <span className="font-medium text-blue-900">{selected.size} selected</span>
          <Button size="sm" variant="outline" onClick={() => runBulk("apply")}
            data-testid="btn-bulk-apply">{tr("Apply Match")}</Button>
          <Button size="sm" variant="outline" onClick={() => runBulk("reprocess")}
            data-testid="btn-bulk-reprocess">{tr("Re-parse")}</Button>
          <Button size="sm" variant="outline" onClick={() => runBulk("flag")}
            data-testid="btn-bulk-flag">{tr("Flag")}</Button>
          <Button size="sm" variant="outline" className="text-red-600 border-red-200"
            onClick={() => runBulk("dismiss")} data-testid="btn-bulk-dismiss">{tr("Dismiss")}</Button>
        </div>
      )}

      </StickyTop>

      <div className="data-table-container overflow-x-auto">
        <Table>
          <TableHeader className="thead-sticky">
            <TableRow className="bg-slate-50">
              <TableHead className="w-8">
                <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length}
                  onChange={toggleAll} data-testid="checkbox-select-all" />
              </TableHead>
              <TableHead className="text-xs">{tr("Received")}</TableHead>
              <TableHead className="text-xs">{tr("From")}</TableHead>
              <TableHead className="text-xs">{tr("Subject")}</TableHead>
              <TableHead className="text-xs">{tr("Action")}</TableHead>
              <TableHead className="text-xs">{tr("PO #")}</TableHead>
              <TableHead className="text-xs">{tr("Status")}</TableHead>
              <TableHead className="text-xs">{tr("Summary")}</TableHead>
              <TableHead className="text-xs w-24">{tr("Actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-slate-400 py-8 text-sm"
                  data-testid="inbox-empty-state">
                  {loading ? "Loading…" : "No inbox items yet."}
                </TableCell>
              </TableRow>
            ) : filtered.map(row => (
              <TableRow key={row.id} data-testid={`inbox-row-${row.id}`}
                className={selected.has(row.id) ? "bg-blue-50/60" : ""}>
                <TableCell>
                  <input type="checkbox" checked={selected.has(row.id)}
                    onChange={() => toggleRow(row.id)}
                    data-testid={`checkbox-inbox-${row.id}`} />
                </TableCell>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                  {row.received_at ? new Date(row.received_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell className="text-xs">
                  <div className="font-medium text-slate-700">{row.from_name || row.from_email}</div>
                  {row.from_name && (
                    <div className="text-slate-400">{row.from_email}</div>
                  )}
                </TableCell>
                <TableCell className="text-xs max-w-xs truncate" title={row.subject}>
                  {row.subject || <span className="text-slate-400">(no subject)</span>}
                </TableCell>
                <TableCell className="text-xs">
                  {ACTION_LABELS[row.classification?.action] || "—"}
                </TableCell>
                <TableCell className="text-xs font-mono">
                  {row.classification?.po_number || "—"}
                </TableCell>
                <TableCell>
                  <StatusChip status={row.status} />
                </TableCell>
                <TableCell className="text-xs text-slate-600 max-w-sm truncate"
                  title={row.applied_action_summary || row.classification?.vendor_message}>
                  {row.applied_action_summary || row.classification?.vendor_message || "—"}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                      onClick={async () => {
                        await reprocessPurchasingInbox(row.id);
                        toast.success("Re-parsed");
                        load();
                      }}
                      data-testid={`btn-row-reprocess-${row.id}`}>
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost"
                      className="h-7 px-2 text-xs text-red-600 hover:bg-red-50"
                      onClick={async () => {
                        await dismissPurchasingInbox(row.id);
                        toast.success("Dismissed");
                        load();
                      }}
                      data-testid={`btn-row-dismiss-${row.id}`}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// PO Dashboard Tab
// ─────────────────────────────────────────────────────────────────────


// This grid starts at Ordered. Draft / Pending / Awaiting approval / Approved live on the WO and the approval queue —
// a PO only lands here once it has actually gone to the vendor.
const ORDERED_PO = ["open", "ordered", "acknowledged"];
const OPEN_PO = [...ORDERED_PO, "shipped", "partial"];
// Default list (no pill on) = ordered + partial (until complete). Pills toggle on/off:
// Status / WO (group) · Shipped = ordered with a vendor ship date | Partial · Received (archived; cancelled POs only via search).
const PO_VIEWS = [
  { key: "shipped", label: "Shipped", title: "Vendor has shipped — on its way" },
  { key: "partial", label: "Partial", title: "Partially received — these also stay in the default list until complete" },
  { key: "received", label: "Received", dividerBefore: true, title: "Archived — fully received" },
];
const inPOView = (po, view) => {
  if (view === "shipped") return po.status === "shipped";
  if (view === "partial") return po.status === "partial";
  if (view === "received") return po.status === "received" || po.status === "closed";
  if (view === "cancelled") return po.status === "cancelled";
  return OPEN_PO.includes(po.status);
};
const fmtQty = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(2));
// Per-line receiving state, matched against what the warehouse checked in.
const lineState = (li) => {
  const ordered = Number(li.qty || 0);
  const got = Number(li.received_qty || 0);
  if (li.received || (ordered > 0 && got >= ordered)) return { key: "received", label: "Received", cls: "bg-emerald-50 text-emerald-700" };
  if (got > 0) return { key: "partial", label: `Partial · ${fmtQty(got)} of ${fmtQty(ordered)}`, cls: "bg-amber-50 text-amber-700" };
  return { key: "missing", label: "Not received", cls: "bg-rose-50 text-rose-700" };
};
const poHaystack = (po) => [
  po.po_number, po.manufacturer_name, po.wo_number, po.wo_title, po.client_name, po.project_manager,
  PO_STATUS_LABEL[po.status] || po.status, po.vendor_quote_number, po.notes, po.vendor_ship_date, po.eta,
  po.tracking_number, po.carrier, po.vendor_order_number,
  ...(po.line_items || []).flatMap((li) => [li.description, li.part_number]),
].filter(Boolean).join(" ").toLowerCase();
const poWoName = (po) => po.wo_title || (po.wo_number ? `WO ${po.wo_number}` : "No work order");
const poSortValue = (po, field) => {
  switch (field) {
    case "po_number": return (po.po_number || "").toLowerCase();
    case "vendor": return (po.manufacturer_name || "").toLowerCase();
    case "wo": return (po.wo_title || "").toLowerCase();
    case "customer": return (po.client_name || "").toLowerCase();
    case "total": return po.total || 0;
    case "status": { const i = PO_STATUS_ORDER.indexOf(po.status); return i === -1 ? PO_STATUS_ORDER.length : i; }
    case "received": return po.items_total ? (po.items_received || 0) / po.items_total : -1;
    case "ack": return po.acknowledged_at ? 1 : 0;
    case "ship_date": return po.vendor_ship_date || "";
    case "eta": return po.eta || "";
    case "tracking": return (po.tracking_number || "").toLowerCase();
    default: return "";
  }
};

// Hoisted so the header never remounts on a parent re-render.
const POTh = ({ field, children, sort, onSort, className = "", filter }) => (
  <TableHead className={`text-xs ${className}`}>
    <SortableHeader label={children} sorted={sort.field === field} direction={sort.dir} onSort={() => onSort(field)} testId={`po-sort-${field}`} filter={filter} />
  </TableHead>
);

const PO_COLS = 11;

// The status pill IS the status control (Ordered and on — earlier steps happen on the WO / approval queue).
// Received = receive everything still open (inventory + events via check-in); Partial opens the receive form for quantities.
const PO_STATUS_OPTIONS = [
  { value: "ordered", label: "Ordered" }, { value: "shipped", label: "Shipped" }, { value: "partial", label: "Partial" },
  { value: "received", label: "Received" }, { value: "cancelled", label: "Cancelled" },
];
const POStatusMenu = ({ po, onPick }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button type="button" title={tr("Click to change status")} data-testid={`po-list-status-${po.id}`}
        className={`inline-flex min-w-[5.5rem] items-center justify-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${PO_STATUS_TONE[po.status] || "bg-slate-100 text-slate-600"}`}>
        {PO_STATUS_LABEL[po.status] || po.status || "—"}<ChevronDown className="h-2.5 w-2.5 opacity-60" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" data-testid={`po-list-status-menu-${po.id}`}>
      {PO_STATUS_OPTIONS.filter((o) => o.value !== po.status).map((o) => (
        <DropdownMenuItem key={o.value} onClick={() => onPick(po, o.value)} data-testid={`po-list-status-option-${o.value}-${po.id}`}>{o.label}</DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

// Expanded PO: one row per line with quantities and dollars, so what's outstanding is obvious.
// Lump-sum (quoteline) POs carry the quoted total on the footer only; lines are the breakout.
const TH = "py-1 pr-3 text-right";
const POLinesRow = ({ po }) => {
  const eco = poEconomics(po);
  const $ = (v) => (v == null ? "—" : money(v));
  const t = eco.totals;
  return (
    <TableRow className="bg-slate-50/60 hover:bg-slate-50/60" data-testid={`po-lines-${po.id}`}>
      <TableCell colSpan={PO_COLS} className="p-0">
        <div className="px-10 py-2">
          {eco.lines.length === 0 ? (
            <div className="text-xs text-slate-400">{tr("No line items on this PO.")}</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[11px] font-semibold text-slate-600">
                  <th className="w-[120px] py-1 pr-3 text-left">{tr("Part #")}</th>
                  <th className="py-1 pr-3 text-left">{tr("Description")}</th>
                  <th className="w-[56px] py-1 pr-3 text-left">{tr("UOM")}</th>
                  <th className={`w-[72px] ${TH}`}>{tr("Ordered")}</th>
                  <th className={`w-[72px] ${TH}`}>{tr("Received")}</th>
                  <th className={`w-[80px] ${TH}`}>{tr("Remaining")}</th>
                  <th className={`w-[90px] ${TH}`}>{tr("Unit Cost")}</th>
                  <th className={`w-[100px] ${TH}`}>{tr("Total")}</th>
                  <th className={`w-[100px] ${TH}`}>Received $</th>
                  <th className={`w-[110px] ${TH}`}>{tr("Outstanding")}</th>
                  <th className="w-[150px] py-1 text-left">{tr("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {eco.lines.map(({ li, qty, got, rem, unitCost, total, receivedValue, outstanding }, i) => {
                  const st = lineState(li);
                  const lid = li.id || i;
                  const open = rem > 0 && st.key !== "received";
                  return (
                    <tr key={lid} className="border-t border-slate-200/70" data-testid={`po-line-${po.id}-${lid}`}>
                      <td className="py-1 pr-3 font-mono text-slate-600">{li.part_number || "—"}</td>
                      <td className="py-1 pr-3 text-slate-700">{li.description || "—"}</td>
                      <td className="py-1 pr-3 text-slate-500">{li.units || "ea"}</td>
                      <td className={`${TH} num`}>{fmtQty(qty)}</td>
                      <td className={`${TH} num`}>{fmtQty(got)}</td>
                      <td className={`${TH} num ${open ? "font-semibold text-rose-600" : "text-slate-400"}`}>{open ? fmtQty(rem) : "—"}</td>
                      <td className={`${TH} num text-slate-600`} data-testid={`po-line-unit-${po.id}-${lid}`}>{eco.lump ? <span className="text-slate-400">lump</span> : $(unitCost)}</td>
                      <td className={`${TH} num`}>{$(total)}</td>
                      <td className={`${TH} num text-emerald-700`}>{$(receivedValue)}</td>
                      <td className={`${TH} num ${outstanding > 0 ? "font-semibold text-rose-600" : "text-slate-400"}`} data-testid={`po-line-outstanding-${po.id}-${lid}`}>{$(outstanding)}</td>
                      <td className="py-1">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${st.cls}`} data-testid={`po-line-status-${po.id}-${lid}`}>{st.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-300 text-[11px] font-semibold text-slate-700" data-testid={`po-lines-totals-${po.id}`}>
                  <td className="py-1 pr-3" colSpan={3}>{eco.lump ? "Lump sum — per vendor quote" : "PO totals"}</td>
                  <td className={`${TH} num`}>{fmtQty(t.ordered)}</td>
                  <td className={`${TH} num`}>{fmtQty(t.received)}</td>
                  <td className={`${TH} num ${t.remaining > 0 ? "text-rose-600" : "text-slate-400"}`}>{t.remaining > 0 ? fmtQty(t.remaining) : "—"}</td>
                  <td className={TH} />
                  <td className={`${TH} num`} data-testid={`po-lines-total-${po.id}`}>{money(t.total)}</td>
                  <td className={`${TH} num text-emerald-700`} data-testid={`po-lines-received-${po.id}`}>{money(t.receivedValue)}</td>
                  <td className={`${TH} num ${t.outstanding > 0 ? "text-rose-600" : "text-slate-400"}`} data-testid={`po-lines-outstanding-${po.id}`}>{money(t.outstanding)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

function POsTab() {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receivingPO, setReceivingPO] = useState(null);
  const [shipmentPO, setShipmentPO] = useState(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("");
  const [groupBy, setGroupBy] = useState(""); // "" | "status" | "wo"
  const [colFilters, setColFilters] = useState({ wo: "", vendor: "", customer: "", status: "" });
  const setColFilter = (k, v) => setColFilters((p) => ({ ...p, [k]: v || "" }));
  const [expanded, setExpanded] = useState(() => new Set());
  const [sort, setSort] = useState({ field: "", dir: "asc" });
  const openWO = (po) => {
    if (!po.wo_id) return;
    setFocusTarget(FOCUS_PO, po.id);
    navigate("/clients", { state: { openClientId: po.client_id, openTab: "workorders", openWorkOrderId: po.wo_id, openWoTab: "purchase_orders", returnTo: "/purchasing?tab=pos" } });
  };
  // Status pill picks. Received = check everything in; Partial = enter quantities; otherwise a lifecycle move.
  const changeStatus = async (po, status) => {
    try {
      if (status === "received" && openLines(po).length) {
        await checkInProcurementPO(po.id, openLines(po).map((li) => ({ line_item_id: li.id, qty: null })), "");
        toast.success(`${po.po_number} received`);
      } else if (status === "partial") {
        setReceivingPO(po);
        return;
      } else {
        await patchPOStatus(po.id, status);
        toast.success(`${po.po_number} → ${PO_STATUS_LABEL[status] || status}`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
  };


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPurchasingPOs({ limit: 1000 });
      setAll(res.data?.rows || []);
    } catch {
      toast.error("Failed to load POs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSort = (field) => setSort((p) => (p.field === field ? { field, dir: p.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" }));

  // A search looks across every grouping; otherwise the pill decides the view. Column filters apply on top.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cf = colFilters;
    let out = all.filter((po) => (q ? poHaystack(po).includes(q) : inPOView(po, view)))
      .filter((po) => (!cf.wo || poWoName(po) === cf.wo) && (!cf.vendor || (po.manufacturer_name || "—") === cf.vendor)
        && (!cf.customer || (po.client_name || "—") === cf.customer) && (!cf.status || po.status === cf.status));
    if (sort.field) {
      out = [...out].sort((a, b) => {
        const av = poSortValue(a, sort.field); const bv = poSortValue(b, sort.field);
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return out;
  }, [all, search, view, sort, colFilters]);

  // Header filter menus list what is actually in the current view (before column filters).
  const filterOpts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = all.filter((po) => (q ? poHaystack(po).includes(q) : inPOView(po, view)));
    const uniq = (fn) => [...new Set(base.map(fn).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v }));
    return {
      wo: uniq(poWoName), vendor: uniq((po) => po.manufacturer_name || "—"), customer: uniq((po) => po.client_name || "—"),
      status: [...new Set(base.map((po) => po.status).filter(Boolean))].sort((a, b) => PO_STATUS_ORDER.indexOf(a) - PO_STATUS_ORDER.indexOf(b)).map((s) => ({ value: s, label: PO_STATUS_LABEL[s] || s })),
    };
  }, [all, search, view]);

  // Grouping — by status (lifecycle order) or by work order name; current sort kept within each group.
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const by = new Map();
    const keyOf = groupBy === "wo" ? poWoName : (po) => po.status || "—";
    rows.forEach((po) => { const k = keyOf(po); if (!by.has(k)) by.set(k, []); by.get(k).push(po); });
    const idx = (s) => { const i = PO_STATUS_ORDER.indexOf(s); return i === -1 ? PO_STATUS_ORDER.length : i; };
    const entries = [...by.entries()];
    if (groupBy === "status") entries.sort((a, b) => idx(a[0]) - idx(b[0]));
    else entries.sort((a, b) => a[0].localeCompare(b[0]));
    return entries.map(([key, list]) => ({ status: key, label: groupBy === "status" ? (PO_STATUS_LABEL[key] || key) : key, rows: list }));
  }, [rows, groupBy]);

  const toggleExpanded = (id) => setExpanded((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pickView = (key) => { setSearch(""); setView((cur) => (cur === key ? "" : key)); };

  const total = useMemo(() => rows.reduce((s, po) => s + (po.total || 0), 0), [rows]);

  const allOpen = rows.length > 0 && rows.every((po) => expanded.has(po.id));
  const toggleAllExpanded = () => setExpanded(allOpen ? new Set() : new Set(rows.map((po) => po.id)));

  const renderRow = (po) => {
    const isOpen = expanded.has(po.id);
    const lines = (po.line_items || []).length;
    return (
      <Fragment key={po.id}>
      <TableRow data-testid={`po-row-${po.id}`} className={isOpen ? "bg-brand/5" : ""}>
        <TableCell className="px-1">
          <button type="button" onClick={() => toggleExpanded(po.id)} aria-expanded={isOpen} aria-label={isOpen ? "Collapse line items" : "Expand line items"}
            title={lines ? `${isOpen ? "Hide" : "Show"} ${lines} line item${lines === 1 ? "" : "s"}` : "No line items"}
            className="inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`po-expand-${po.id}`}>
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </TableCell>
        <TableCell className="font-mono text-xs font-semibold text-brand whitespace-nowrap">
          {po.wo_id ? (
            <button type="button" className="hover:underline" onClick={() => openWO(po)} title={`Open ${po.wo_number} and jump to this PO`} data-testid={`po-open-${po.id}`}>{po.po_number || "—"}</button>
          ) : (po.po_number || "—")}
        </TableCell>
        <TableCell className="max-w-[280px] truncate text-xs text-slate-700" title={po.wo_title || ""} data-testid={`po-list-wo-${po.id}`}>
          {po.wo_title || "—"}
        </TableCell>
        <TableCell className="text-xs text-slate-600">{po.manufacturer_name || "—"}</TableCell>
        <TableCell className="text-xs text-slate-600">{po.client_name || "—"}</TableCell>
        <TableCell className="text-xs font-medium num">{money(po.total)}</TableCell>
        <TableCell><POStatusMenu po={po} onPick={changeStatus} /></TableCell>
        <TableCell className="text-xs text-slate-600 num" data-testid={`po-list-received-${po.id}`}>
          {po.items_total ? `${po.items_received}/${po.items_total}` : "—"}
        </TableCell>
        <TableCell className="text-xs text-slate-500 num">{shortDate(po.vendor_ship_date)}</TableCell>
        <TableCell className="text-xs text-slate-500 num" data-testid={`po-list-eta-${po.id}`}>{shortDate(po.eta)}</TableCell>
        <TableCell className="text-xs whitespace-nowrap">
          <span className="inline-flex items-center gap-1.5">
            <TrackingLink po={po} compact testId={`po-list-tracking-${po.id}`} />
            {!["received", "closed", "cancelled"].includes(po.status) && (
              <button type="button" className="text-slate-400 hover:text-slate-700" title={tr("Edit ship date / ETA / tracking")} onClick={() => setShipmentPO(po)} data-testid={`po-list-shipment-${po.id}`}>
                <Pencil className="h-3 w-3" />
              </button>
            )}
          </span>
        </TableCell>
      </TableRow>
      {isOpen && <POLinesRow po={po} />}
      </Fragment>
    );
  };

  const hdr = { sort, onSort };
  const searching = search.trim().length > 0;

  return (
    <div className="space-y-2" data-testid="pos-tab">
      <StickyTop slot="b" testId="pos-sticky-top">
      <div className="flex flex-wrap items-center gap-2" data-testid="po-list-toolbar">
        <SearchBar value={search} onChange={setSearch} testId="input-po-search" placeholder={tr("Search...")} />
        <PillRail testId="select-po-status">
          <Pill on={groupBy === "status"} onClick={() => setGroupBy((v) => (v === "status" ? "" : "status"))} testId="po-group-status" title={groupBy === "status" ? "Stop grouping" : "Group the list by status"}>{tr("Status")}</Pill>
          <Pill on={groupBy === "wo"} onClick={() => setGroupBy((v) => (v === "wo" ? "" : "wo"))} testId="po-group-wo" title={groupBy === "wo" ? "Stop grouping" : "Group the list by work order"}>WO</Pill>
          {PO_VIEWS.map((o) => (
            <Fragment key={o.key}>
              {o.dividerBefore && <PillDivider />}
              <Pill on={!searching && view === o.key} title={view === o.key ? "Back to the open list" : o.title} testId={`po-status-${o.key}`} onClick={() => pickView(o.key)}>{o.label}</Pill>
            </Fragment>
          ))}
        </PillRail>
        <div className="ml-auto text-xs text-slate-500" data-testid="pos-count">
          <span className="num">{rows.length}</span> {searching ? "matching" : (view ? PO_VIEWS.find((o) => o.key === view)?.label.toLowerCase() : "open")} PO{rows.length === 1 ? "" : "s"} · <span className="num">{money(total)}</span>
        </div>
      </div>

      </StickyTop>

      <div className="data-table-container">
        <Table data-testid="po-list-table">
          <TableHeader className="thead-sticky">
            <TableRow>
              <TableHead className="w-6 px-1">
                <button type="button" onClick={toggleAllExpanded} disabled={rows.length === 0} aria-label={allOpen ? tr("Collapse all") : tr("Expand all")} title={allOpen ? tr("Collapse all") : tr("Expand all")}
                  className="inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40" data-testid="po-expand-all">
                  {allOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
              </TableHead>
              <POTh {...hdr} field="po_number" className="w-[130px]">{tr("PO #")}</POTh>
              <POTh {...hdr} field="wo" className="min-w-[200px]" filter={<HeaderFilter label={tr("Name")} testId="po-filter-wo" options={filterOpts.wo} value={colFilters.wo} onChange={(v) => setColFilter("wo", v)} />}>{tr("Name")}</POTh>
              <POTh {...hdr} field="vendor" className="min-w-[160px]" filter={<HeaderFilter label={tr("Vendor")} testId="po-filter-vendor" options={filterOpts.vendor} value={colFilters.vendor} onChange={(v) => setColFilter("vendor", v)} />}>{tr("Vendor")}</POTh>
              <POTh {...hdr} field="customer" className="min-w-[160px]" filter={<HeaderFilter label={tr("Customer")} testId="po-filter-customer" options={filterOpts.customer} value={colFilters.customer} onChange={(v) => setColFilter("customer", v)} />}>{tr("Customer")}</POTh>
              <POTh {...hdr} field="total" className="w-[110px] num">{tr("Total")}</POTh>
              <POTh {...hdr} field="status" className="w-[130px]" filter={<HeaderFilter label={tr("Status")} testId="po-filter-status" options={filterOpts.status} value={colFilters.status} onChange={(v) => setColFilter("status", v)} />}>{tr("Status")}</POTh>
              <POTh {...hdr} field="received" className="w-[90px] num">{tr("Received")}</POTh>
              <POTh {...hdr} field="ship_date" className="w-[100px] num">{tr("Ship Date")}</POTh>
              <POTh {...hdr} field="eta" className="w-[90px] num">{tr("ETA")}</POTh>
              <POTh {...hdr} field="tracking" className="w-[100px]">{tr("Tracking")}</POTh>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={PO_COLS} className="py-8 text-center text-sm text-slate-400" data-testid="pos-empty-state">
                  {loading ? "Loading…" : "No POs match the current filters."}
                </TableCell>
              </TableRow>
            ) : grouped ? grouped.map((g) => (
              <Fragment key={g.status}>
                <TableRow className="bg-slate-50 hover:bg-slate-50" data-testid={`po-group-row-${g.status}`}>
                  <TableCell colSpan={PO_COLS} className="py-1.5 text-[11px] font-semibold text-slate-600">
                    {g.label} <span className="font-normal text-slate-400 num">{g.rows.length}</span>
                  </TableCell>
                </TableRow>
                {g.rows.map(renderRow)}
              </Fragment>
            )) : rows.map(renderRow)}
          </TableBody>
        </Table>
      </div>
      {receivingPO && <ReceivePOModal po={receivingPO} onClose={() => setReceivingPO(null)} onCheckedIn={() => load()} />}
      {shipmentPO && <ShipmentDialog po={shipmentPO} onClose={() => setShipmentPO(null)} onSaved={() => load()} />}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Page shell
// ─────────────────────────────────────────────────────────────────────


export default function Purchasing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useAuth();
  // Tabs follow the access matrix: Purchase Orders + Inbox = ops.purchasing, Receiving = ops.warehouse
  // (so warehouse staff can be given just that tab).
  const tabs = useMemo(() => [
    can("ops.purchasing", "read") && { key: "pos", label: tr("Purchase Orders"), testId: "tab-pos" },
    can("ops.warehouse", "read") && { key: "receiving", label: tr("Receiving"), testId: "tab-receiving" },
    can("ops.purchasing", "read") && { key: "inbox", label: tr("Inbox"), testId: "tab-inbox" },
  ].filter(Boolean), [can]);
  const [tab, setTab] = useState(() => {
    const wanted = searchParams.get("tab");
    return tabs.some((t) => t.key === wanted) ? wanted : (tabs[0]?.key || "pos");
  });
  // Keep ?tab= in step with the active tab (deep links + refresh land on the same tab).
  const onTab = (key) => {
    setTab(key);
    setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", key); return n; });
  };
  useEffect(() => {
    const wanted = searchParams.get("tab");
    if (wanted && wanted !== tab && tabs.some((t) => t.key === wanted)) setTab(wanted);
  }, [searchParams, tabs]); // eslint-disable-line react-hooks/exhaustive-deps
  const [m365Status, setM365Status] = useState(null);

  const refreshM365 = useCallback(async () => {
    try {
      const res = await getM365Status();
      setM365Status(res.data);
    } catch {
      setM365Status({ configured: false, connected: false });
    }
  }, []);

  useEffect(() => {
    refreshM365();
    // OAuth callback flash messages.
    const m365Param = searchParams.get("m365");
    if (m365Param === "connected") {
      toast.success("M365 mailbox connected!");
      setSearchParams({});
    } else if (m365Param === "error") {
      const reason = searchParams.get("reason") || "unknown";
      toast.error(`M365 connection failed: ${reason}`);
      setSearchParams({});
    }
  }, [refreshM365, searchParams, setSearchParams]);

  return (
    <div className="space-y-4" data-testid="purchasing-page">
      <StickyTop testId="purchasing-sticky-top">
      <SubTabs testId="purchasing-tabs" active={tab} onChange={onTab} className="px-0" tabs={tabs} />
      </StickyTop>
      {tab === "inbox" && <InboxTab m365Status={m365Status} onM365Changed={refreshM365} />}
      {tab === "pos" && <POsTab />}
      {tab === "receiving" && <ReceivingTab />}
    </div>
  );
}
